// Google Picker API

interface GooglePickerDocument {
  id: string
  name: string
  mimeType: string
  sizeBytes: number
  type: string
  url: string
}

interface GooglePickerCallbackData {
  action: string
  docs: GooglePickerDocument[]
}

declare global {
  interface Window {
    gapi?: {
      load: (
        api: string,
        config: { callback: () => void; onerror: () => void },
      ) => void
    }
  }

  namespace google.picker {
    enum ViewId {
      DOCS = 'all',
    }
    enum Feature {
      MULTISELECT_ENABLED = 'multiselectEnabled',
      SUPPORT_DRIVES = 'supportDrives',
    }
    enum Action {
      PICKED = 'picked',
      CANCEL = 'cancel',
    }
    interface Picker {
      setVisible(visible: boolean): void
      dispose(): void
    }
    class DocsView {
      constructor(viewId?: ViewId)
      setIncludeFolders(include: boolean): this
      setSelectFolderEnabled(enabled: boolean): this
      setEnableDrives(enabled: boolean): this
    }
    class PickerBuilder {
      setOAuthToken(token: string): this
      setDeveloperKey(key: string): this
      setAppId(appId: string): this
      addView(view: DocsView): this
      enableFeature(feature: Feature): this
      setCallback(callback: (data: GooglePickerCallbackData) => void): this
      build(): Picker
    }
  }
}

export function useGooglePicker() {
  const config = useRuntimeConfig()
  const { addToast } = useToastNotifications()

  const isPickerOpen = ref(false)

  function loadPickerApi(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (window.google?.picker) {
        resolve()
        return
      }

      const existing = document.querySelector(
        'script[src="https://apis.google.com/js/api.js"]',
      )
      if (existing) {
        if (window.gapi) {
          window.gapi.load('picker', {
            callback: () => resolve(),
            onerror: () =>
              reject(new Error('Failed to load Google Picker API')),
          })
        } else {
          existing.addEventListener('load', () => {
            window.gapi!.load('picker', {
              callback: () => resolve(),
              onerror: () =>
                reject(new Error('Failed to load Google Picker API')),
            })
          })
        }
        return
      }

      const script = document.createElement('script')
      script.src = 'https://apis.google.com/js/api.js'
      script.async = true
      script.defer = true
      script.onload = () => {
        window.gapi!.load('picker', {
          callback: () => resolve(),
          onerror: () => reject(new Error('Failed to load Google Picker API')),
        })
      }
      script.onerror = () =>
        reject(new Error('Failed to load Google Picker API'))
      document.head.appendChild(script)
    })
  }

  async function openPicker(
    accessToken: string,
  ): Promise<GooglePickerDocument[] | null> {
    try {
      await loadPickerApi()
    } catch {
      addToast({
        type: 'error',
        heading: 'Google Drive',
        message: 'Failed to load Google Picker. Please try again.',
      })
      return null
    }

    const apiKey = config.public.google?.googlePickerApiKey as string
    if (!apiKey) {
      addToast({
        type: 'error',
        heading: 'Google Drive',
        message: 'Google Picker is not configured.',
      })
      return null
    }

    return new Promise((resolve) => {
      isPickerOpen.value = true

      const docsView = new google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)

      const sharedDrivesView = new google.picker.DocsView()
        .setIncludeFolders(true)
        .setSelectFolderEnabled(true)
        .setEnableDrives(true)

      const picker = new google.picker.PickerBuilder()
        .setOAuthToken(accessToken)
        .setDeveloperKey(apiKey)
        .addView(docsView)
        .addView(sharedDrivesView)
        .enableFeature(google.picker.Feature.MULTISELECT_ENABLED)
        .enableFeature(google.picker.Feature.SUPPORT_DRIVES)
        .setCallback((data: GooglePickerCallbackData) => {
          if (data.action === google.picker.Action.PICKED) {
            const docs: GooglePickerDocument[] = data.docs.map((doc) => ({
              id: doc.id,
              name: doc.name,
              mimeType: doc.mimeType,
              sizeBytes: doc.sizeBytes,
              type: doc.type,
              url: doc.url,
            }))
            console.info('[Google Picker] Selected files:', docs)
            isPickerOpen.value = false
            resolve(docs)
          } else if (data.action === google.picker.Action.CANCEL) {
            isPickerOpen.value = false
            resolve(null)
          }
        })
        .build()

      picker.setVisible(true)
    })
  }

  return {
    isPickerOpen: readonly(isPickerOpen),
    openPicker,
  }
}
